import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Gift, Sparkles, Heart } from "lucide-react";
import { purchaseVoucher } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import { IMAGES } from "@/data/content";

const PRICE = 35;

export default function GiftVouchers() {
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "",
    recipient_name: "", recipient_email: "",
    seats: 1, message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = Number(form.seats || 0) * PRICE;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_email) {
      toast.error("We need your name and email to send the voucher");
      return;
    }
    if (form.seats < 1 || form.seats > 10) {
      toast.error("Choose between 1 and 10 seats");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        recipient_name: form.recipient_name || null,
        recipient_email: form.recipient_email || null,
        seats: Number(form.seats),
        message: form.message || null,
        origin_url: window.location.origin,
      };
      const res = await purchaseVoucher(payload);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      toast.error("Payment provider not ready. Please try again shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="gift-vouchers-page">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[340px] overflow-hidden">
        <img src={IMAGES.parliament} alt="Parliament House" className="absolute inset-0 w-full h-full object-cover sepia-soft" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">Gift Vouchers</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">Give the gift of Marvellous Melbourne.</h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            A two-hour walking tour makes a wonderful birthday, anniversary or Christmas gift.
            Send a beautifully designed PDF voucher — instantly.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10">
        {/* FORM */}
        <form onSubmit={submit} className="lg:col-span-3 card-warm rounded-2xl p-8" data-testid="voucher-form">
          <div className="stamp">Buy a gift voucher</div>
          <h2 className="serif text-3xl text-[#6E2638] mt-4">Your details</h2>
          <OrnamentalDivider />

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="buyer-name">Your full name</Label>
              <Input id="buyer-name" data-testid="input-buyer-name" value={form.buyer_name}
                onChange={(e) => update("buyer_name", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="buyer-email">Your email</Label>
              <Input id="buyer-email" type="email" data-testid="input-buyer-email" value={form.buyer_email}
                onChange={(e) => update("buyer_email", e.target.value)} required />
            </div>
          </div>

          <h3 className="serif text-2xl text-[#6E2638] mt-8">Recipient (optional)</h3>
          <p className="text-xs text-[#1A1817]/70 mb-3">Leave blank to send the voucher only to yourself.</p>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="rec-name">Recipient's name</Label>
              <Input id="rec-name" data-testid="input-rec-name" value={form.recipient_name}
                onChange={(e) => update("recipient_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rec-email">Recipient's email</Label>
              <Input id="rec-email" type="email" data-testid="input-rec-email" value={form.recipient_email}
                onChange={(e) => update("recipient_email", e.target.value)} />
            </div>
          </div>

          <h3 className="serif text-2xl text-[#6E2638] mt-8">Gift details</h3>
          <div className="grid md:grid-cols-2 gap-5 mt-2">
            <div>
              <Label htmlFor="seats">Number of seats</Label>
              <Input id="seats" type="number" min={1} max={10} data-testid="input-seats" value={form.seats}
                onChange={(e) => update("seats", e.target.value)} />
              <p className="text-xs text-[#1A1817]/60 mt-1">$35 per seat · 1–10 seats per voucher</p>
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="message">Personal message (optional)</Label>
            <Textarea id="message" data-testid="input-message" rows={3} maxLength={280}
              placeholder="Happy birthday — knew you'd love this…"
              value={form.message} onChange={(e) => update("message", e.target.value)} />
            <p className="text-xs text-[#1A1817]/60 mt-1">{form.message.length}/280 characters — printed on the voucher</p>
          </div>

          <div className="mt-8 rounded-2xl bg-[#F5EFEB] border border-[#E5D9C5] p-5 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">You'll pay</div>
              <div className="serif text-3xl text-[#6E2638]" data-testid="voucher-total">
                AUD ${total.toFixed(2)}
              </div>
              <div className="text-xs text-[#1A1817]/70 mt-1">
                {form.seats} × ${PRICE} · secured via Stripe
              </div>
            </div>
            <Gift size={32} className="text-[#6E2638] opacity-70" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full justify-center" data-testid="submit-voucher">
            {submitting ? (
              <><Loader2 className="animate-spin" size={18} /> Redirecting to secure checkout…</>
            ) : (
              <>Buy voucher &amp; send <Sparkles size={16} /></>
            )}
          </button>

          <p className="text-xs text-[#1A1817]/60 mt-4 text-center italic">
            After payment, we email the voucher PDF to you (and the recipient, if you added them).
          </p>
        </form>

        {/* SIDEBAR */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="card-warm rounded-2xl p-8">
            <Heart size={24} className="text-[#6E2638]" />
            <h3 className="serif text-2xl text-[#6E2638] mt-3">How it works</h3>
            <ol className="mt-4 space-y-3 text-sm text-[#1A1817]/85 list-decimal list-inside">
              <li>Choose how many seats — $35 each.</li>
              <li>Pay securely via Stripe.</li>
              <li>We instantly email a beautifully-designed PDF voucher with a unique code.</li>
              <li>The recipient enters the code at <b>/individual</b> to book their walk — free.</li>
            </ol>
          </div>

          <div className="card-warm rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">The fine print</div>
            <ul className="mt-3 space-y-2 text-sm text-[#1A1817]/80 list-disc list-inside">
              <li>Vouchers do not expire.</li>
              <li>Non-refundable, but transferable — pass the code to anyone.</li>
              <li>1–10 seats per voucher · pick any of Martin's scheduled walks.</li>
              <li>Any questions? <a href="mailto:Mmmwt35@gmail.com" className="text-[#6E2638] underline">Email Martin</a>.</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
